import * as z from "zod";

export const REPLO_AUTH_ISSUER = "replo-auth";

export const REPLO_BEDROCK_WS_PROTOCOL = "replo.bedrock.v1";
export const REPLO_BEARER_WS_PROTOCOL_PREFIX = "replo.bearer.";

export const reploJwtSourceSchema = z
  .enum([
    "browser",
    "miniapp",
    "sandbox",
    "preview",
    "sandbox-handle",
    "files-viewer",
  ])
  .describe("ReploJwtSource");

// One in-box service per handle; a stolen handle is only good for its own route. The service decides the carrier: a bearer token, or a SigV4 pair for the S3 routes.
// TODO (Gabe, 2026-09-23, REPL-32599): drop "raindrop" once every coordinator is on a build that no longer mints it; it stays only so stored handles and in-flight mints from older coordinators still parse.
export const bearerHandleServiceSchema = z
  .enum(["loki", "raindrop", "braintrust"])
  .describe("BearerHandleService");
// Replo's own R2 buckets behind the proxy's S3 routes: litestream's session backups and rclone's publish artifacts.
export const sigV4HandleServiceSchema = z
  .enum(["sessionBackups", "publishArtifacts"])
  .describe("SigV4HandleService");

const baseJwtClaimsSchema = z.object({
  sub: z.uuid(),
  iss: z.literal(REPLO_AUTH_ISSUER),
  exp: z.number(),
  iat: z.number(),
  jti: z.string(),
});

const browserPrivateClaimsSchema = z.object({
  source: z.literal("browser"),
});

const filesViewerPrivateClaimsSchema = z.object({
  source: z.literal("files-viewer"),
  anonymous: z.boolean(),
  // The one file or folder this token opens. A served file's code can read its own token, so the token must open nothing else.
  // TODO (Yuxin, 2026-10-31, REPL-34994): make required once shells that mint without it are gone and their tokens have expired.
  fileId: z.uuid().optional(),
});

const miniappPrivateClaimsSchema = z.object({
  source: z.literal("miniapp"),
  projectId: z.uuid(),
  miniappId: z.string(),
});

const sandboxPrivateClaimsSchema = z.object({
  source: z.literal("sandbox"),
  projectId: z.uuid(),
  sandboxId: z.string(),
});

const previewPrivateClaimsSchema = z.object({
  source: z.literal("preview"),
  sandboxId: z.string(),
  port: z.number().int().positive(),
});

// Replo Secure Handle: minted once per incarnation for processes that cannot follow the 6h JWT rotation; no exp, the denylist is its revocation.
const sandboxHandlePrivateClaimsSchema = z.object({
  source: z.literal("sandbox-handle"),
  service: bearerHandleServiceSchema,
  projectId: z.uuid(),
  sandboxId: z.string(),
  // Reserved for the postmark: SHA-256 of the box's egress secret, 128 bits, base64url; unset until postmarks ship.
  postmarkHash: z.string().optional(),
});

// NOTE (Max, 2026-05-26): Replo-specific per-source private claims that a
// signer must supply when minting a Replo JWT.
export const reploJwtPrivateClaimsSchema = z
  .discriminatedUnion("source", [
    browserPrivateClaimsSchema,
    filesViewerPrivateClaimsSchema,
    miniappPrivateClaimsSchema,
    sandboxPrivateClaimsSchema,
    previewPrivateClaimsSchema,
    sandboxHandlePrivateClaimsSchema,
  ])
  .describe("ReploJwtPrivateClaims");

// NOTE (Max, 2026-05-26): The full JWT payload a verifier sees (when verifying a Replo JWT). This payload is
// built by merging each private-claims arm with `baseJwtClaimsSchema`.
export const reploJwtPayloadSchema = z
  .discriminatedUnion("source", [
    browserPrivateClaimsSchema.extend(baseJwtClaimsSchema.shape),
    filesViewerPrivateClaimsSchema.extend(baseJwtClaimsSchema.shape),
    miniappPrivateClaimsSchema.extend(baseJwtClaimsSchema.shape),
    sandboxPrivateClaimsSchema.extend(baseJwtClaimsSchema.shape),
    previewPrivateClaimsSchema.extend(baseJwtClaimsSchema.shape),
    // No exp: the holder never restarts for a rotation, so expiry would only take it down on a timer.
    sandboxHandlePrivateClaimsSchema
      .extend(baseJwtClaimsSchema.shape)
      .extend({ exp: z.number().optional() }),
  ])
  .describe("ReploJwtPayload");
